import { useState } from 'react';
import { Modal, Form, Alert } from 'react-bootstrap';
import Button from 'components/base/Button';
import { returnAsset, ReturnAssetPayload } from 'api/assetsAPI';

interface ReturnAssetModalProps {
  show: boolean;
  handleClose: () => void;
  asset: {
    id: number;
    name: string;
    serial_number: string;
    current_holder_name?: string;
  } | null;
  onReturnSuccess: () => void;
}

const ReturnAssetModal = ({
  show,
  handleClose,
  asset,
  onReturnSuccess
}: ReturnAssetModalProps) => {
  const [returnReason, setReturnReason] = useState('');
  const [conditionAtReturn, setConditionAtReturn] = useState<
    'excellent' | 'good' | 'fair' | 'poor'
  >('good');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!asset) return;

    setLoading(true);
    setError(null);

    try {
      const payload: ReturnAssetPayload = {
        condition_at_return: conditionAtReturn
      };

      // Only add return_reason if it's not empty
      if (returnReason.trim()) {
        payload.return_reason = returnReason.trim();
      }

      await returnAsset(asset.id, payload);

      // Reset form
      setReturnReason('');
      setConditionAtReturn('good');

      onReturnSuccess();
      handleClose();
    } catch (err) {
      console.error('Failed to return asset:', err);

      // Extract error message
      const axiosError = err as {
        response?: {
          data?: { detail?: string; message?: string };
        };
        message?: string;
      };

      const errorMessage =
        axiosError.response?.data?.detail ||
        axiosError.response?.data?.message ||
        axiosError.message ||
        'Failed to return asset. Please try again.';

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setReturnReason('');
    setConditionAtReturn('good');
    setError(null);
    handleClose();
  };

  if (!asset) return null;

  return (
    <Modal show={show} onHide={handleModalClose} centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fs-6">Return Device</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {/* Asset Information */}
          <div className="mb-3 p-3 bg-body-highlight rounded">
            <h6 className="mb-2 fs-7">Device Information</h6>
            <div className="row">
              <div className="col-6">
                <small className="text-body-tertiary d-block">Asset Name</small>
                <div className="fw-semibold fs-8">{asset.name}</div>
              </div>
              <div className="col-6">
                <small className="text-body-tertiary d-block">
                  Serial Number
                </small>
                <div className="fs-8">{asset.serial_number}</div>
              </div>
              {asset.current_holder_name && (
                <div className="col-12 mt-2">
                  <small className="text-body-tertiary d-block">
                    Current Holder
                  </small>
                  <div className="fs-8">{asset.current_holder_name}</div>
                </div>
              )}
            </div>
          </div>

          {/* Condition at Return */}
          <Form.Group className="mb-3">
            <Form.Label>Condition at Return *</Form.Label>
            <Form.Select
              value={conditionAtReturn}
              onChange={e =>
                setConditionAtReturn(
                  e.target.value as 'excellent' | 'good' | 'fair' | 'poor'
                )
              }
              required
              disabled={loading}
            >
              <option value="excellent">Excellent</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="poor">Poor</option>
            </Form.Select>
            <Form.Text className="text-muted">
              Assess the device's condition upon return
            </Form.Text>
          </Form.Group>

          {/* Return Reason */}
          <Form.Group className="mb-3">
            <Form.Label>Return Reason (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={returnReason}
              onChange={e => setReturnReason(e.target.value)}
              placeholder="e.g., User left company, Project completed, Device upgrade..."
              disabled={loading}
            />
            <Form.Text className="text-muted">
              Provide context for why the device is being returned
            </Form.Text>
          </Form.Group>

          <Alert variant="info" className="mb-0">
            <i className="fas fa-info-circle me-2"></i>
            <small>
              Returning this device will:
              <ul className="mb-0 mt-2">
                <li>Set status to "Available"</li>
                <li>Clear the current holder assignment</li>
                <li>Record the return in assignment history</li>
              </ul>
            </small>
          </Alert>
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <Button
            variant="secondary"
            onClick={handleModalClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Returning...
              </>
            ) : (
              'Return Device'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ReturnAssetModal;
